import { put, call, takeLatest, all, select } from 'redux-saga/effects';
import * as roleSelectors from 'pages/role/controllers/selectors';

import { CustomNotification } from 'common/components';

import * as servicesGrid from 'services/grid';
import * as roleServices from 'services/roles-security';

import * as types from './constants';
import * as actions from './actions';

import {
  mapToRoleMemberGridColDef,
  mapToMemberNotInRoleGridColDef,
} from './mapper/mapToRoleMemberGridColDef';

import { apiHandler } from 'utils/api';

function* getRoleListSaga(action) {
  try {
    const { memberId } = action.payload;
    const params = { memberId };

    const response = yield call(roleServices.getRoleList, params);
    const { isSuccess, data } = response;

    if (isSuccess) {
      yield put(actions.getRoleListSuccess(data?.roleInfos));
    } else {
      yield put(actions.getRoleListFail());
    }
  } catch (error) {
    yield put(actions.getRoleListFail());
  }
}

function* getMemberRoleListSaga(action) {
  try {
    const response = yield call(roleServices.getMemberRoleList);
    const { isSuccess, data, message } = response;

    if (isSuccess) {
      yield put(actions.getMemberRoleListSuccess(data?.memberRoles));
      return true;
    } else {
      yield put(actions.getMemberRoleListFail(message));
      return false;
    }
  } catch (error) {
    yield put(actions.getMemberRoleListFail());
    return false;
  }
}

function* getRolePermissionListSaga(action) {
  try {
    const { roleId } = action.payload;
    const params = { roleId };

    const response = yield call(roleServices.getRolePermissions, params);
    const { isSuccess, data } = response;

    if (isSuccess) {
      yield put(actions.getRolePermissionListSuccess(data?.permissions));
    } else {
      yield put(actions.getRolePermissionListFail());
    }
  } catch (error) {
    yield put(actions.getRolePermissionListFail());
  }
}

export function* getColumnGridMemberRoleSaga(action) {
  try {
    const { gridName } = action.payload;

    const response = yield call(servicesGrid.gridColumnInfo, gridName);
    const columnData = response?.columns;
    const memberRoleListColDef = mapToRoleMemberGridColDef(columnData);

    yield put(actions.gridColumnRoleMembersSuccess(memberRoleListColDef));
  } catch (error) {
    yield put(actions.gridColumnRoleMembersFail(error));
  }
}

export function* getColumnGridMemberNotInRoleSaga(action) {
  try {
    const { gridName } = action.payload;

    const response = yield call(servicesGrid.gridColumnInfo, gridName);
    const columnData = response?.columns;
    const memberNotInRoleListColDef =
      mapToMemberNotInRoleGridColDef(columnData);

    yield put(
      actions.gridColumnMembersNotInRoleSuccess(memberNotInRoleListColDef)
    );
  } catch (error) {
    yield put(actions.gridColumnMembersNotInRoleFail(error));
  }
}

function* getAvailablePermissionsSaga() {
  try {
    const response = yield call(roleServices.getAvailablePermissions);

    if (response?.data?.permissions) {
      yield put(
        actions.getAvailablePermissionSuccess(response?.data?.permissions)
      );
    } else {
      yield put(actions.getAvailablePermissionError(response?.message));
    }
  } catch (error) {
    yield put(actions.getAvailablePermissionError(error));
  }
}

function* getAvailableMemberPermissionsSaga() {
  try {
    const response = yield call(roleServices.getAllMemberPermissionList);

    if (response?.data?.permissionList) {
      yield put(
        actions.getAllMemberPermissionListSuccess(
          response?.data?.permissionList
        )
      );
    } else {
      yield put(actions.getAllMemberPermissionListFail(response?.message));
    }
  } catch (error) {
    yield put(actions.getAllMemberPermissionListFail(error));
  }
}

function* saveMemberRoleSaga(action) {
  let thisErrorCallback;

  try {
    const { formValues, successCallback, errorCallback } = action.payload;

    thisErrorCallback = errorCallback;

    const response = yield call(roleServices.saveMemberRole, formValues);

    const { data, isSuccess, message } = response;

    const savedMemberRoleId = data?.memberRoleId;

    if (isSuccess) {
      const isFetchedRoleListSuccessfully = yield call(getMemberRoleListSaga);

      const memberRoleList = yield select(roleSelectors.selectMemberRoleList());
      const memberRoleListData = memberRoleList?.data;

      if (!isFetchedRoleListSuccessfully) {
        yield call(CustomNotification.error, 'Failed to get member role list');
        return;
      }

      if (savedMemberRoleId) {
        const foundRoleMember = memberRoleListData.find(
          (memberRole) => memberRole?.roleId === savedMemberRoleId
        );

        if (foundRoleMember) {
          yield put(actions.setSelectedMemberRole(foundRoleMember));
        }
      }

      yield call(successCallback);
    } else {
      yield call(errorCallback, message);
    }
  } catch (error) {
    yield call(thisErrorCallback, error);
  }
}

function* deleteMemberRoleSaga(action) {
  let thisErrorCallback;

  try {
    const { params, successCallback, errorCallback } = action.payload;

    thisErrorCallback = errorCallback;

    const response = yield call(roleServices.deleteMemberRole, params);

    const { isSuccess, message } = response;

    if (isSuccess) {
      const isFetchedRoleListSuccessfully = yield call(getMemberRoleListSaga);

      if (!isFetchedRoleListSuccessfully) {
        yield call(CustomNotification.error, 'Failed to get member role list');
        return;
      }

      yield call(successCallback);
      yield put(actions.setSelectedMemberRole(null));
    } else {
      yield call(errorCallback, message);
    }
  } catch (error) {
    yield call(thisErrorCallback, error);
  }
}

function* removeMemberFromRoleSaga(action) {
  const { params, successCallback } = action.payload;

  yield call(apiHandler, {
    service: roleServices.removeMembersFromRole,
    params,
    successMessage: 'Remove member(s) from role successfully',
    errorMessage: 'Failed to remove member(s) from role',
    successCallback,
  });
}

function* watchAll() {
  yield all([
    takeLatest(types.GET_ROLE_LIST, getRoleListSaga),
    takeLatest(types.GET_ROLE_PERMISSION_LIST, getRolePermissionListSaga),
    takeLatest(types.GET_AVAILABLE_PERMISSIONS, getAvailablePermissionsSaga),

    takeLatest(types.GET_MEMBER_ROLE_LIST, getMemberRoleListSaga),
    takeLatest(
      types.GET_ALL_MEMBER_PERMISSION_LIST,
      getAvailableMemberPermissionsSaga
    ),
    takeLatest(types.SAVE_MEMBER_ROLE, saveMemberRoleSaga),
    takeLatest(types.DELETE_MEMBER_ROLE, deleteMemberRoleSaga),
    takeLatest(
      types.GET_COLUMNS_GRID_ROLE_MEMBERS,
      getColumnGridMemberRoleSaga
    ),
    takeLatest(
      types.GET_COLUMNS_GRID_MEMBERS_NOT_IN_ROLE,
      getColumnGridMemberNotInRoleSaga
    ),
    takeLatest(types.REMOVE_MEMBER_FROM_ROLE, removeMemberFromRoleSaga),
  ]);
}

export default watchAll;
